/**
 * Step 8: Review, Preview & Edit
 *
 * Merged from the former Step8ReviewPreview + Step9EditDocument.
 * - Review summary of all entered data
 * - Rendered preview using the shared lease-document.css pipeline (not iframe)
 * - Inline per-section editing with hover affordance
 * - "Edit full document" escape hatch (MDEditor source-only mode)
 * - Per-section Modified badge + Reset to Original
 * - Document-level Reset All
 * - Anchor info banner and anchor validation error
 *
 * Single-renderer rule (BR-17): ALL rendered HTML goes through
 *   convertAnchorsToHtml → marked → DOMPurify → dangerouslySetInnerHTML
 * The MDEditor built-in preview pane is NEVER used for rendering.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { MarkdownThemeScope } from '../../../components/MarkdownThemeScope';
import '../../../components/rules/md-editor-overrides.css';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { InlineSectionEditor } from '../../../components/lease-inputs/InlineSectionEditor';
import { renderMarkdownWithTocAndAnchors } from '../../../components/lease-inputs/lease-render';
import type {
  WizardState,
  PreviewResponse,
  AssembledContentSnapshot,
} from '../../../types/lease-generation';
import type { TenantWithDetails } from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { JurisdictionCoverageStatement } from '../../../components/JurisdictionCoverageStatement';
import { LocalRuleNote } from '../../../components/LocalRuleNote';
import { Badge } from '../../../components/ui/badge';
import { formatCalendarDate } from '../../../utils/date';
import { asCalendarDateString } from '@resida/shared';

// ---------------------------------------------------------------------------
// Focus trap — mirrors Modal.tsx implementation
// ---------------------------------------------------------------------------

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Re-export LeaseSection for consumers
export type { LeaseSection } from './section-utils';

interface DocumentEntry {
  id: string;
  title: string;
  type: 'lease' | 'disclosure' | 'addendum';
}

// ---------------------------------------------------------------------------
// Section splicing helpers
// ---------------------------------------------------------------------------

// Pure functions extracted to section-utils.ts for testability
import { splitIntoSections, spliceSection } from './section-utils';

// ---------------------------------------------------------------------------
// EditedContent helpers
// ---------------------------------------------------------------------------

function previewToSnapshot(preview: PreviewResponse): AssembledContentSnapshot {
  return {
    coreLease: preview.coreLease,
    disclosures: preview.disclosures.map(d => ({ id: d.id, content: d.content })),
    addendums: preview.addendums.map(a => ({ slug: a.slug, content: a.content })),
  };
}

function getActiveDocContent(
  activeDocId: string,
  editedContent: AssembledContentSnapshot | null,
  previewContent: PreviewResponse | null,
): string {
  const source = editedContent ?? (previewContent ? previewToSnapshot(previewContent) : null);
  if (!source) return '';
  if (activeDocId === 'core-lease') return source.coreLease;
  const disc = source.disclosures.find(d => `disclosure-${d.id}` === activeDocId);
  if (disc) return disc.content;
  const add = source.addendums.find(a => `addendum-${a.slug}` === activeDocId);
  if (add) return add.content;
  return '';
}

function getOriginalDocContent(
  activeDocId: string,
  previewContent: PreviewResponse | null,
): string {
  if (!previewContent) return '';
  if (activeDocId === 'core-lease') return previewContent.coreLease;
  const disc = previewContent.disclosures.find(d => `disclosure-${d.id}` === activeDocId);
  if (disc) return disc.content;
  const add = previewContent.addendums.find(a => `addendum-${a.slug}` === activeDocId);
  if (add) return add.content;
  return '';
}

function isDocModified(
  activeDocId: string,
  editedContent: AssembledContentSnapshot | null,
  previewContent: PreviewResponse | null,
): boolean {
  if (!editedContent || !previewContent) return false;
  const editedDoc = getActiveDocContent(activeDocId, editedContent, null);
  const originalDoc = getOriginalDocContent(activeDocId, previewContent);
  return editedDoc !== originalDoc;
}

function anyDocModified(
  editedContent: AssembledContentSnapshot | null,
  previewContent: PreviewResponse | null,
): boolean {
  if (!editedContent || !previewContent) return false;
  return (
    editedContent.coreLease !== previewContent.coreLease ||
    editedContent.disclosures.some(
      (d, i) => d.content !== (previewContent.disclosures[i]?.content ?? ''),
    ) ||
    editedContent.addendums.some(
      (a, i) => a.content !== (previewContent.addendums[i]?.content ?? ''),
    )
  );
}

function updateActiveDocContent(
  activeDocId: string,
  newContent: string,
  editedContent: AssembledContentSnapshot,
): AssembledContentSnapshot {
  if (activeDocId === 'core-lease') {
    return { ...editedContent, coreLease: newContent };
  }
  const isDisc = editedContent.disclosures.some(d => `disclosure-${d.id}` === activeDocId);
  if (isDisc) {
    return {
      ...editedContent,
      disclosures: editedContent.disclosures.map(d =>
        `disclosure-${d.id}` === activeDocId ? { ...d, content: newContent } : d,
      ),
    };
  }
  return {
    ...editedContent,
    addendums: editedContent.addendums.map(a =>
      `addendum-${a.slug}` === activeDocId ? { ...a, content: newContent } : a,
    ),
  };
}

// ---------------------------------------------------------------------------
// Step 8 props
// ---------------------------------------------------------------------------

interface Step8Props {
  state: WizardState;
  unitTenants: TenantWithDetails[];
  previewLease: () => Promise<boolean>;
  goToStep: (n: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9) => void;
  setEditedContent: (content: AssembledContentSnapshot | null) => void;
}

// ---------------------------------------------------------------------------
// Step 8 root component
// ---------------------------------------------------------------------------

export function Step8ReviewPreview({
  state,
  unitTenants,
  previewLease,
  goToStep,
  setEditedContent,
}: Step8Props) {
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const { inputs, selectedTenantIds, previewContent, editedContent, isLoading, error } = state;

  // The preview response carries coverage; before a preview runs, the Step 1 lookup has the same value.
  const coverage = previewContent?.coverage ?? state.jurisdictionInfo?.coverage;

  const selectedTenants = unitTenants.filter(t => selectedTenantIds.includes(t.id));

  const handlePreview = async () => {
    const success = await previewLease();
    if (success) setShowPreviewModal(true);
  };

  // When preview panel opens and editedContent is null but previewContent is available,
  // initialize editedContent from previewContent (same behavior as former Step 9).
  useEffect(() => {
    if (previewContent && !editedContent) {
      setEditedContent(previewToSnapshot(previewContent));
    }
  }, [previewContent, editedContent, setEditedContent]);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-base font-semibold text-foreground mb-1">Review, Preview & Edit</h3>
        <p className="text-sm text-muted-foreground">
          Review all lease details and preview or edit the document before generating.
        </p>
      </div>

      <JurisdictionCoverageStatement coverage={coverage} subject="lease" />

      {/* Anchor info banner */}
      <div
        data-testid="anchor-info-banner"
        className="p-4 bg-warning/10 border border-warning/30 rounded-lg flex items-start gap-3"
        role="note"
      >
        <span className="text-warning text-lg leading-tight shrink-0" aria-hidden="true">!</span>
        <p className="text-sm text-warning m-0">
          <strong>Important:</strong> Signature anchor tokens like{' '}
          <code className="text-xs bg-warning/20 px-1 py-0.5 rounded font-mono">
            [[SIGNATURE:landlord]]
          </code>{' '}
          appear as plain text in the editor. Do not delete them — they are required for digital
          signing. Clicking Next will validate that all required tokens are still present.
        </p>
      </div>

      {/* Anchor validation error */}
      {error && (
        <div
          data-testid="anchor-validation-error"
          className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg"
          role="alert"
        >
          <p className="text-sm font-semibold text-destructive mb-1">Validation Error</p>
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Step 2: Landlord Contact Summary */}
      <div data-testid="step-summary-2" className="bg-accent/50 rounded-lg border border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-foreground">Landlord Contact</h4>
          <Button type="button" variant="link" onClick={() => goToStep(2)} className="text-xs text-primary p-0 h-auto">
            Edit
          </Button>
        </div>
        <dl className="flex flex-col gap-1">
          <div className="flex justify-between text-sm">
            <dt className="text-muted-foreground">Name</dt>
            <dd className="font-medium text-foreground">{state.landlordContact.fullName || '—'}</dd>
          </div>
          <div className="flex justify-between text-sm">
            <dt className="text-muted-foreground">Email</dt>
            <dd className="font-medium text-foreground">{state.landlordContact.email || '—'}</dd>
          </div>
          <div className="flex justify-between text-sm">
            <dt className="text-muted-foreground">Contact Methods</dt>
            <dd className="font-medium text-foreground">
              {state.landlordContact.contactMethods.length > 0
                ? state.landlordContact.contactMethods.join(', ')
                : '—'}
            </dd>
          </div>
        </dl>
      </div>

      {/* Step 3: Tenants Summary */}
      <div data-testid="step-summary-3" className="bg-accent/50 rounded-lg border border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-foreground">Tenants</h4>
          <Button type="button" variant="link" onClick={() => goToStep(3)} className="text-xs text-primary p-0 h-auto">
            Edit
          </Button>
        </div>
        {selectedTenants.length > 0 ? (
          <ul className="flex flex-col gap-1">
            {selectedTenants.map(t => (
              <li key={t.id} className="text-sm text-foreground">
                {t.firstName} {t.lastName} ({t.email})
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-warning">No tenants selected</p>
        )}
      </div>

      {/* Step 4: Lease Terms Summary */}
      <div data-testid="step-summary-4" className="bg-accent/50 rounded-lg border border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-foreground">Lease Terms</h4>
          <Button type="button" variant="link" onClick={() => goToStep(4)} className="text-xs text-primary p-0 h-auto">
            Edit
          </Button>
        </div>
        <dl className="flex flex-col gap-1">
          <div className="flex justify-between text-sm">
            <dt className="text-muted-foreground">Lease Type</dt>
            <dd className="font-medium text-foreground capitalize">
              {inputs.leaseType?.replace('-', ' ') ?? '—'}
            </dd>
          </div>
          <div className="flex justify-between text-sm">
            <dt className="text-muted-foreground">Start Date</dt>
            <dd className="font-medium text-foreground">
              {formatCalendarDate(inputs.startDate == null ? inputs.startDate : asCalendarDateString(inputs.startDate))}
            </dd>
          </div>
          {inputs.leaseType === 'fixed-term' && (
            <div className="flex justify-between text-sm">
              <dt className="text-muted-foreground">End Date</dt>
              <dd className="font-medium text-foreground">
                {formatCalendarDate(inputs.endDate == null ? inputs.endDate : asCalendarDateString(inputs.endDate))}
              </dd>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <dt className="text-muted-foreground">Auto-renewal</dt>
            <dd className="font-medium text-foreground">{inputs.autoRenewal ? 'Yes' : 'No'}</dd>
          </div>
        </dl>
      </div>

      {/* Step 5: Financial Terms Summary */}
      <div data-testid="step-summary-5" className="bg-accent/50 rounded-lg border border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-foreground">Financial Terms</h4>
          <Button type="button" variant="link" onClick={() => goToStep(5)} className="text-xs text-primary p-0 h-auto">
            Edit
          </Button>
        </div>
        <dl className="flex flex-col gap-1">
          <div className="flex justify-between text-sm">
            <dt className="text-muted-foreground">Monthly Rent</dt>
            <dd className="font-medium text-foreground">
              {inputs.rentAmount != null ? `$${inputs.rentAmount.toLocaleString()}` : '—'}
            </dd>
          </div>
          <div className="flex justify-between text-sm">
            <dt className="text-muted-foreground">Security Deposit</dt>
            <dd className="font-medium text-foreground">
              {inputs.securityDeposit != null ? `$${inputs.securityDeposit.toLocaleString()}` : '—'}
            </dd>
          </div>
          <div className="flex justify-between text-sm">
            <dt className="text-muted-foreground">Late Fee</dt>
            <dd className="font-medium text-foreground">
              {inputs.lateFeeAmount != null ? `$${inputs.lateFeeAmount.toLocaleString()}` : '—'}
            </dd>
          </div>
          <div className="flex justify-between text-sm">
            <dt className="text-muted-foreground">Grace Period</dt>
            <dd className="font-medium text-foreground">
              {inputs.gracePeriodDays != null ? `${inputs.gracePeriodDays} days` : '—'}
            </dd>
          </div>
          <div className="flex justify-between text-sm">
            <dt className="text-muted-foreground">Payment Due Day</dt>
            <dd className="font-medium text-foreground">
              {inputs.paymentDueDay != null ? `Day ${inputs.paymentDueDay}` : '—'}
            </dd>
          </div>
        </dl>
      </div>

      {/* Step 5b: Additional Charges */}
      <div data-testid="step-summary-5b" className="bg-accent/50 rounded-lg border border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-foreground">Additional Charges</h4>
          <Button type="button" variant="link" onClick={() => goToStep(5)} className="text-xs text-primary p-0 h-auto">
            Edit
          </Button>
        </div>
        <dl className="flex flex-col gap-1">
          <div className="flex justify-between text-sm">
            <dt className="text-muted-foreground">Parking</dt>
            <dd className="font-medium text-foreground">{state.parking.included ? 'Included' : 'Not included'}</dd>
          </div>
          {state.parking.included && state.parking.monthlyFee != null && state.parking.monthlyFee > 0 && (
            <div className="flex justify-between text-sm">
              <dt className="text-muted-foreground">Monthly Parking Fee</dt>
              <dd className="font-medium text-foreground">${state.parking.monthlyFee}</dd>
            </div>
          )}
          {state.utilities.includedInRent.length > 0 && (
            <div className="flex justify-between text-sm">
              <dt className="text-muted-foreground">Included in Rent</dt>
              <dd className="font-medium text-foreground">{state.utilities.includedInRent.join(', ')}</dd>
            </div>
          )}
          {state.utilities.chargedBack.length > 0 && (
            <div className="flex justify-between text-sm">
              <dt className="text-muted-foreground">Charged Back</dt>
              <dd className="font-medium text-foreground">{state.utilities.chargedBack.join(', ')}</dd>
            </div>
          )}
          {state.utilities.tenantArranged.length > 0 && (
            <div className="flex justify-between text-sm">
              <dt className="text-muted-foreground">Tenant Arranges</dt>
              <dd className="font-medium text-foreground">{state.utilities.tenantArranged.join(', ')}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Step 6: Policies Summary */}
      <div data-testid="step-summary-6" className="bg-accent/50 rounded-lg border border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-foreground">Policies & Rules</h4>
          <Button type="button" variant="link" onClick={() => goToStep(6)} className="text-xs text-primary p-0 h-auto">
            Edit
          </Button>
        </div>
        <dl className="flex flex-col gap-1">
          <div className="flex justify-between text-sm">
            <dt className="text-muted-foreground">Entry Notice</dt>
            <dd className="font-medium text-foreground">
              {inputs.entryNoticeHours != null ? `${inputs.entryNoticeHours} hours` : '—'}
            </dd>
          </div>
          <div className="flex justify-between text-sm">
            <dt className="text-muted-foreground">Subletting</dt>
            <dd className="font-medium text-foreground capitalize">
              {inputs.sublettingPolicy?.replace('-', ' ') ?? '—'}
            </dd>
          </div>
          <div className="flex justify-between text-sm">
            <dt className="text-muted-foreground">Short-Term Rental</dt>
            <dd className="font-medium text-foreground">
              {state.shortTermRentalProhibited ? 'Prohibited' : 'Not restricted'}
            </dd>
          </div>
        </dl>
      </div>

      {/* Step 7: Pet Details Summary */}
      <div data-testid="step-summary-7" className="bg-accent/50 rounded-lg border border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-foreground">Pet Details</h4>
          <Button type="button" variant="link" onClick={() => goToStep(7)} className="text-xs text-primary p-0 h-auto">
            Edit
          </Button>
        </div>
        <dl className="flex flex-col gap-1">
          <div className="flex justify-between text-sm">
            <dt className="text-muted-foreground">Pets Allowed</dt>
            <dd className="font-medium text-foreground">{state.pets.allowed ? 'Yes' : 'No'}</dd>
          </div>
          {state.pets.allowed && state.pets.animals.length > 0 && (
            <div className="flex justify-between text-sm">
              <dt className="text-muted-foreground">Pets on Lease</dt>
              <dd className="font-medium text-foreground">
                {state.pets.animals.map(a => a.name || 'Unnamed').join(', ')}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Required Disclosures (from preview) */}
      <LocalRuleNote coverage={coverage} category="required_disclosures" />
      {previewContent?.disclosures && previewContent.disclosures.length > 0 && (
        <div
          data-testid="disclosures-list"
          className="bg-primary/10 rounded-lg border border-primary/20 p-4"
        >
          <h4 className="text-sm font-semibold text-primary mb-3">Required Disclosures</h4>
          <ul className="flex flex-col gap-1">
            {previewContent.disclosures.map(d => (
              <li key={d.id} className="flex items-start gap-2 text-sm text-primary">
                <span className="text-primary/60 mt-0.5">•</span>
                <span>
                  <strong>{d.title}</strong>
                  {' — '}
                  <span className="text-primary/80">
                    Required by {previewContent.jurisdictionDisplay} law
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Preview Button */}
      <Button
        type="button"
        data-testid="preview-button"
        onClick={handlePreview}
        disabled={isLoading}
        className="w-full min-h-[44px]"
      >
        {isLoading ? 'Loading preview...' : 'Preview Document'}
      </Button>

      {/* Full-page Document Preview with inline editing */}
      {showPreviewModal && previewContent && (
        <LeasePreviewViewer
          previewContent={previewContent}
          editedContent={editedContent}
          setEditedContent={setEditedContent}
          onClose={() => setShowPreviewModal(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LeasePreviewViewer — full-page overlay with inline section editing
// ---------------------------------------------------------------------------

interface LeasePreviewViewerProps {
  previewContent: PreviewResponse;
  editedContent: AssembledContentSnapshot | null;
  setEditedContent: (content: AssembledContentSnapshot | null) => void;
  onClose: () => void;
}

function LeasePreviewViewer({
  previewContent,
  editedContent,
  setEditedContent,
  onClose,
}: LeasePreviewViewerProps) {
  // Build document list for sidebar
  const documents: DocumentEntry[] = [];
  if (previewContent.coreLease) {
    documents.push({ id: 'core-lease', title: 'Lease Agreement', type: 'lease' });
  }
  previewContent.disclosures.forEach(d => {
    documents.push({ id: `disclosure-${d.id}`, title: d.title, type: 'disclosure' });
  });
  previewContent.addendums.forEach(a => {
    documents.push({ id: `addendum-${a.slug}`, title: a.title, type: 'addendum' });
  });

  const [activeDocId, setActiveDocId] = useState(documents[0]?.id ?? '');
  const [showFullDocEditor, setShowFullDocEditor] = useState(false);
  const [fullDocDraft, setFullDocDraft] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    isDestructive: boolean;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    isDestructive: false,
    onConfirm: () => {},
  });

  // Reset section editor when switching documents
  useEffect(() => {
    setShowFullDocEditor(false);
  }, [activeDocId]);

  // Focus management: move focus into overlay on mount; restore on unmount.
  // Mirrors Modal.tsx pattern for WCAG 2.4.3 compliance.
  useEffect(() => {
    const previousFocus = document.activeElement;
    const raf = requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });
    return () => {
      cancelAnimationFrame(raf);
      if (previousFocus && previousFocus instanceof HTMLElement) {
        previousFocus.focus();
      }
    };
  }, []);

  // Keyboard handler: Escape to close + Tab focus trap.
  // Mirrors Modal.tsx handleKeyDown for WCAG 2.1.1, 2.1.2, 2.4.3 compliance.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key !== 'Tab' || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
      ).filter((el) => el.offsetParent !== null); // exclude hidden elements

      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose],
  );

  // Register keyboard listener while overlay is mounted
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  // Get current document content (from editedContent or previewContent)
  const activeDocContent = getActiveDocContent(activeDocId, editedContent, previewContent);

  // Split into sections for inline editing
  const sections = splitIntoSections(activeDocContent);

  // TOC: render the full document to extract heading entries
  const { toc } = renderMarkdownWithTocAndAnchors(activeDocContent);

  // Active document entry for header
  const activeDoc = documents.find(d => d.id === activeDocId);

  // TOC scroll — uses containerRef instead of iframe
  const scrollToHeading = (headingId: string) => {
    if (!containerRef.current) return;
    const el = containerRef.current.querySelector(`#${CSS.escape(headingId)}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // -------------------------------------------------------------------------
  // Section edit handlers
  // -------------------------------------------------------------------------

  // Section saves from InlineSectionEditor hand back the full spliced document.
  function handleSectionsChange(updatedFull: string) {
    if (!editedContent) return;
    setEditedContent(updateActiveDocContent(activeDocId, updatedFull, editedContent));
  }

  function handleResetSection(index: number) {
    if (!editedContent || !previewContent) return;

    const sectionName = sections[index]?.heading ?? 'this section';
    setConfirmDialog({
      isOpen: true,
      title: 'Reset Section',
      message: `Reset "${sectionName}" to the original assembled content? Your edits will be lost.`,
      confirmText: 'Reset',
      isDestructive: true,
      onConfirm: () => {
        const originalFull = getOriginalDocContent(activeDocId, previewContent);
        const origSections = splitIntoSections(originalFull);
        const origSection = origSections[index];
        if (!origSection) return;

        const currentFull = getActiveDocContent(activeDocId, editedContent, previewContent);
        const currentSections = splitIntoSections(currentFull);
        const currentSection = currentSections[index];
        if (!currentSection) return;

        const updatedFull = spliceSection(
          currentFull,
          currentSection.startIndex,
          currentSection.endIndex,
          origSection.content,
        );
        setEditedContent(updateActiveDocContent(activeDocId, updatedFull, editedContent));
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
    });
  }

  function handleResetAll() {
    if (!previewContent) return;
    setConfirmDialog({
      isOpen: true,
      title: 'Reset All Sections',
      message: 'Reset ALL sections to the original assembled content? All your edits will be lost.',
      confirmText: 'Reset All',
      isDestructive: true,
      onConfirm: () => {
        setEditedContent(previewToSnapshot(previewContent));
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
      },
    });
  }

  // -------------------------------------------------------------------------
  // Full-doc editor handlers
  // -------------------------------------------------------------------------

  function handleOpenFullDocEditor() {
    setFullDocDraft(activeDocContent);
    setShowFullDocEditor(true);
  }

  function handleFullDocSave() {
    if (!editedContent) return;
    setEditedContent(updateActiveDocContent(activeDocId, fullDocDraft, editedContent));
    setShowFullDocEditor(false);
  }

  function handleFullDocCancel() {
    setShowFullDocEditor(false);
    setFullDocDraft('');
  }

  // -------------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------------

  const hasAnyModified = anyDocModified(editedContent, previewContent);
  const activeDocIsModified = isDocModified(activeDocId, editedContent, previewContent);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-muted z-50 flex flex-col" data-testid="lease-preview-viewer" role="dialog" aria-modal="true" aria-labelledby="lease-preview-heading">
      {/* Header */}
      <div className="bg-card border-b border-border px-6 py-3 flex items-center justify-between shrink-0 gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 id="lease-preview-heading" className="text-lg font-semibold text-foreground m-0">Lease Preview</h2>
          {previewContent.validationErrors && previewContent.validationErrors.length > 0 && (
            <Badge variant="warning">
              {previewContent.validationErrors.length} warning
              {previewContent.validationErrors.length !== 1 ? 's' : ''}
            </Badge>
          )}
          {hasAnyModified && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="reset-all-button"
              onClick={handleResetAll}
              className="min-h-[36px] text-xs"
            >
              Reset All to Original
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!showFullDocEditor && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="edit-full-document-button"
              onClick={handleOpenFullDocEditor}
              className="min-h-[36px] text-xs"
            >
              Edit full document
            </Button>
          )}
          <Button ref={closeButtonRef} variant="outline" data-testid="close-preview-button" onClick={onClose} className="min-h-[36px]">
            Close Preview
          </Button>
        </div>
      </div>

      {/* Validation warnings */}
      {previewContent.validationErrors && previewContent.validationErrors.length > 0 && (
        <div className="bg-warning/10 border-b border-warning/20 px-6 py-2 shrink-0">
          <ul className="flex flex-wrap gap-x-4 gap-y-1">
            {previewContent.validationErrors.map((ve, i) => (
              <li key={i} className="text-xs text-warning">
                {ve.field ? `${ve.field}: ` : ''}{ve.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Main content area */}
      <div className="flex flex-1 min-h-0">
        {/* Document sidebar */}
        <div className="w-64 bg-card border-r border-border overflow-y-auto shrink-0 hidden md:block">
          <div className="p-4">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Documents
            </h3>
            <nav className="flex flex-col gap-1">
              {documents.map(doc => {
                const docModified = isDocModified(doc.id, editedContent, previewContent);
                return (
                  <button
                    key={doc.id}
                    onClick={() => setActiveDocId(doc.id)}
                    className={`text-left px-3 py-2 text-sm rounded-md transition-colors min-h-[36px] ${
                      doc.id === activeDocId
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-foreground hover:bg-accent'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                          doc.type === 'lease'
                            ? 'bg-primary'
                            : doc.type === 'disclosure'
                            ? 'bg-warning'
                            : 'bg-success'
                        }`}
                      />
                      <span className="truncate flex-1">{doc.title}</span>
                      {docModified && (
                        <span className="w-2 h-2 rounded-full bg-warning shrink-0" title="Modified" />
                      )}
                    </div>
                  </button>
                );
              })}
            </nav>

            {/* Legend */}
            <div className="mt-6 pt-4 border-t border-border">
              <div className="flex flex-col gap-1.5 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" /> Lease
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-warning" /> Disclosure
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-success" /> Addendum
                </div>
              </div>
            </div>

            {/* Table of Contents */}
            {toc.length > 0 && (
              <div className="mt-6 pt-4 border-t border-border">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  Table of Contents
                </h3>
                <nav className="flex flex-col gap-0.5" data-testid="lease-preview-toc">
                  {toc.map(entry => (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => scrollToHeading(entry.id)}
                      className={`text-left text-xs text-muted-foreground hover:text-primary hover:bg-primary/5 rounded px-2 py-1 transition-colors truncate ${
                        entry.level === 1
                          ? 'font-medium text-foreground'
                          : entry.level === 2
                          ? 'pl-4'
                          : 'pl-6 text-muted-foreground'
                      }`}
                      title={entry.title}
                    >
                      {entry.title}
                    </button>
                  ))}
                </nav>
              </div>
            )}
          </div>
        </div>

        {/* Mobile document selector */}
        <div className="md:hidden bg-card border-b border-border px-4 py-2 shrink-0 w-full sticky top-0 z-10 flex flex-col gap-1">
          <label htmlFor="mobile-document-selector" className="text-xs font-medium text-muted-foreground">
            Select document
          </label>
          <select
            id="mobile-document-selector"
            value={activeDocId}
            onChange={e => setActiveDocId(e.target.value)}
            className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background text-foreground min-h-[44px]"
            aria-label="Select document"
          >
            {documents.map(doc => {
              const docModified = isDocModified(doc.id, editedContent, previewContent);
              return (
                <option key={doc.id} value={doc.id}>
                  {doc.title}{docModified ? ' [Modified]' : ''}
                </option>
              );
            })}
          </select>
        </div>

        {/* Document content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto p-6 md:p-10">
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              {/* Document header */}
              {activeDoc && (
                <div className="px-8 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <span
                      className={`text-xs font-medium uppercase tracking-wider ${
                        activeDoc.type === 'lease'
                          ? 'text-primary'
                          : activeDoc.type === 'disclosure'
                          ? 'text-warning'
                          : 'text-success'
                      }`}
                    >
                      {activeDoc.type}
                    </span>
                    <h3 className="text-lg font-semibold text-foreground mt-1 m-0">
                      {activeDoc.title}
                    </h3>
                  </div>
                  {activeDocIsModified && (
                    <span className="text-xs text-warning font-medium">Modified</span>
                  )}
                </div>
              )}

              {/* Full-document editor mode */}
              {showFullDocEditor ? (
                <div className="p-4 flex flex-col gap-3">
                  <MarkdownThemeScope aria-label="Full document editor">
                    <MDEditor
                      value={fullDocDraft}
                      onChange={(val) => setFullDocDraft(val ?? '')}
                      height={600}
                      preview="edit"
                    />
                  </MarkdownThemeScope>
                  <div className="flex gap-2 justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleFullDocCancel}
                      className="min-h-[44px] text-sm"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleFullDocSave}
                      className="min-h-[44px] text-sm"
                    >
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                /* Inline section-by-section view */
                <div ref={containerRef} data-testid="lease-preview-container">
                  <InlineSectionEditor
                    key={activeDocId}
                    original={getOriginalDocContent(activeDocId, previewContent)}
                    value={activeDocContent}
                    onChange={handleSectionsChange}
                    onReset={handleResetSection}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        isDestructive={confirmDialog.isDestructive}
      />
    </div>
  );
}
